import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';

import { fetchEndpoints, type Endpoint } from './controlApi';
import { ApiError, fetchAppliances, fetchSites, type Appliance, type Site } from './fleetApi';
import {
  deleteTarget,
  endSession,
  fetchSessions,
  fetchTargets,
  fetchTranscript,
  formatBytes,
  formatDuration,
  fromBase64,
  openSession,
  remoteErrorText,
  saveTarget,
  toAscii,
  toHex,
  transcriptText,
  type AccessTarget,
  type Frame,
  type RemoteSession,
  type TargetInput,
  type TargetProtocol,
} from './remoteApi';
import { RemoteTerminal } from './RemoteTerminal';
import { routePath } from './router';
import { ConfirmDialog } from './shared/ConfirmDialog';
import { EmptyState } from './shared/EmptyState';
import { FormDialog } from './shared/FormDialog';
import { PageHeader } from './shared/PageHeader';

// The shell's terminal emulator (xterm.js) loads only when a shell opens.
const RemoteShell = lazy(() => import('./RemoteShell').then((m) => ({ default: m.RemoteShell })));

/** Sessions and targets refresh this often while the page is open. */
const REFRESH_MS = 15_000;

interface RemoteAccessProps {
  apiUrl: string;
  tenantId: string;
  siteId: string;
  token: string;
  permissions: string[];
  /** Platform operators may open an appliance's emergency shell. */
  platformOperator: boolean;
  /** Sign in again (opening a shell needs a recent sign-in), then come back. */
  onReauthenticate: () => void;
}

type Load =
  | { kind: 'loading' }
  | { kind: 'missing' }
  | { kind: 'error'; error: string }
  | {
      kind: 'ready';
      site: Site;
      targets: AccessTarget[];
      sessions: RemoteSession[];
      appliances: Appliance[];
      endpoints: Endpoint[];
    };

/** An open terminal or shell in this page. */
interface Console {
  session: RemoteSession;
  token: string;
  driver: string | null;
}

/**
 * A site's remote access (UI-006, RAX): the targets each appliance may reach
 * on the site network, opening them (web UIs in a new tab, TCP ports in a
 * terminal), the emergency shell for platform operators, and every session
 * with who, when, why, and how much.
 */
export function RemoteAccess({
  apiUrl,
  tenantId,
  siteId,
  token,
  permissions,
  platformOperator,
  onReauthenticate,
}: RemoteAccessProps) {
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [notice, setNotice] = useState<{
    severity: 'error' | 'info';
    text: React.ReactNode;
  } | null>(null);
  const [editing, setEditing] = useState<AccessTarget | 'new' | null>(null);
  const [deleting, setDeleting] = useState<AccessTarget | null>(null);
  const [ending, setEnding] = useState<RemoteSession | null>(null);
  const [transcript, setTranscript] = useState<RemoteSession | null>(null);
  const [shellOpen, setShellOpen] = useState(false);
  const [opening, setOpening] = useState<string | null>(null);
  const [console_, setConsole] = useState<Console | null>(null);
  const tokenRef = useRef(token);
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  const canWeb = permissions.includes('remote_access.web');
  const canTerminal = permissions.includes('remote_access.terminal');
  const canManage = permissions.includes('endpoint.write');
  const canEndAny = permissions.includes('member.manage') || platformOperator;
  const canReadTranscripts = permissions.includes('audit.read') || platformOperator;

  const reload = useCallback(
    (signal?: AbortSignal) => {
      const t = tokenRef.current;
      return Promise.all([
        fetchSites(apiUrl, t, tenantId, signal),
        fetchTargets(apiUrl, t, tenantId, siteId, signal),
        fetchSessions(apiUrl, t, tenantId, siteId, signal),
        fetchAppliances(apiUrl, t, tenantId, signal),
        fetchEndpoints(apiUrl, t, tenantId, siteId, signal),
      ]).then(([sites, targets, sessions, appliances, endpoints]) => {
        const site = sites.find((s) => s.id === siteId);
        setLoad(
          site
            ? {
                kind: 'ready',
                site,
                targets,
                sessions,
                appliances: appliances.filter((a) => a.site_id === siteId),
                endpoints,
              }
            : { kind: 'missing' },
        );
      });
    },
    [apiUrl, tenantId, siteId],
  );

  useEffect(() => {
    const controller = new AbortController();
    reload(controller.signal).catch((err: unknown) => {
      if (!controller.signal.aborted) setLoad({ kind: 'error', error: remoteErrorText(err) });
    });
    const timer = setInterval(() => {
      reload(controller.signal).catch(() => {
        // Keep showing the last good data; the next refresh retries.
      });
    }, REFRESH_MS);
    return () => {
      controller.abort();
      clearInterval(timer);
    };
  }, [reload]);

  const refresh = () => {
    void reload().catch(() => undefined);
  };

  if (load.kind === 'loading')
    return <Typography color="text.secondary">Loading remote access…</Typography>;
  if (load.kind === 'missing')
    return (
      <Alert severity="warning">This site doesn&apos;t exist or isn&apos;t in your access.</Alert>
    );
  if (load.kind === 'error')
    return <Alert severity="error">Could not load remote access: {load.error}</Alert>;

  const { site, targets, sessions, appliances, endpoints } = load;
  const open = sessions.filter((s) => s.status === 'active' || s.status === 'opening');
  const past = sessions.filter((s) => s.status === 'ended' || s.status === 'failed');

  const openTarget = (t: AccessTarget) => {
    setNotice(null);
    if (t.protocol === 'tcp') {
      setOpening(t.id);
      openSession(apiUrl, tokenRef.current, tenantId, siteId, { target_id: t.id })
        .then((o) => {
          if (o.token) setConsole({ session: o.session, token: o.token, driver: t.driver });
          refresh();
        })
        .catch((err: unknown) => {
          setNotice({
            severity: 'error',
            text: `Could not open ${t.name}: ${remoteErrorText(err)}`,
          });
        })
        .finally(() => {
          setOpening(null);
        });
      return;
    }
    // Open the tab now, inside the click, so it isn't blocked as a popup;
    // point it at the session once the appliance has opened it.
    const tab = window.open('', '_blank');
    setOpening(t.id);
    openSession(apiUrl, tokenRef.current, tenantId, siteId, { target_id: t.id })
      .then((o) => {
        if (!o.url) throw new Error('The server returned no session address.');
        if (tab) {
          tab.opener = null;
          tab.location.href = o.url;
        } else {
          setNotice({
            severity: 'info',
            text: (
              <>
                Your browser blocked the new tab.{' '}
                <Link href={o.url} target="_blank" rel="noopener noreferrer">
                  Open {t.name}
                </Link>{' '}
                (this link works once, for 2 minutes).
              </>
            ),
          });
        }
        refresh();
      })
      .catch((err: unknown) => {
        tab?.close();
        setNotice({ severity: 'error', text: `Could not open ${t.name}: ${remoteErrorText(err)}` });
      })
      .finally(() => {
        setOpening(null);
      });
  };

  return (
    <Box data-testid="remote-page">
      <PageHeader
        crumbs={[
          { label: 'Sites', path: '/' },
          { label: site.name, path: routePath({ name: 'site', siteId }) },
        ]}
        title="Remote access"
        subtitle="Reach devices on the site network through its appliance. Every session is recorded."
        actions={
          <>
            {platformOperator && appliances.length > 0 && (
              <Button
                variant="outlined"
                color="warning"
                onClick={() => {
                  setShellOpen(true);
                }}
                data-testid="open-shell"
              >
                Open shell
              </Button>
            )}
            {canManage && appliances.length > 0 && (
              <Button
                variant="contained"
                onClick={() => {
                  setEditing('new');
                }}
                data-testid="add-target"
              >
                Add target
              </Button>
            )}
          </>
        }
      />
      {notice && (
        <Alert
          severity={notice.severity}
          sx={{ mb: 2 }}
          onClose={() => {
            setNotice(null);
          }}
          data-testid="remote-notice"
        >
          {notice.text}
        </Alert>
      )}

      {console_ && (
        <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, mb: 3 }} data-testid="console">
          {console_.session.kind === 'shell' ? (
            <Suspense fallback={<Typography color="text.secondary">Loading terminal…</Typography>}>
              <RemoteShell
                apiUrl={apiUrl}
                session={console_.session}
                sessionToken={console_.token}
                onEnd={() =>
                  endSession(apiUrl, tokenRef.current, tenantId, siteId, console_.session.id)
                }
                onClose={() => {
                  setConsole(null);
                  refresh();
                }}
              />
            </Suspense>
          ) : (
            <RemoteTerminal
              apiUrl={apiUrl}
              tenantId={tenantId}
              token={token}
              session={console_.session}
              sessionToken={console_.token}
              driver={console_.driver}
              onEnd={() =>
                endSession(apiUrl, tokenRef.current, tenantId, siteId, console_.session.id)
              }
              onClose={() => {
                setConsole(null);
                refresh();
              }}
            />
          )}
        </Paper>
      )}

      <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
        Targets
      </Typography>
      {targets.length === 0 ? (
        <EmptyState title="No targets yet" testId="targets-empty">
          {appliances.length === 0
            ? 'Claim an appliance at this site first: targets are reached through it.'
            : canManage
              ? 'Add the devices on the site network that people may reach remotely, such as the lighting processor’s web interface. The appliance refuses everything else.'
              : 'Nothing at this site can be reached remotely yet. Ask a technician to add a target.'}
        </EmptyState>
      ) : (
        <Stack spacing={1} data-testid="targets">
          {targets.map((t) => {
            const allowed = t.protocol === 'tcp' ? canTerminal : canWeb;
            return (
              <Paper key={t.id} variant="outlined" sx={{ p: 1.5 }} data-testid={`target-${t.name}`}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between' }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ alignItems: 'center', flexWrap: 'wrap' }}
                    >
                      <Typography sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
                        {t.name}
                      </Typography>
                      <Chip
                        size="small"
                        label={t.protocol === 'tcp' ? 'Terminal' : t.protocol.toUpperCase()}
                      />
                    </Stack>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ overflowWrap: 'anywhere' }}
                    >
                      {t.host}:{t.port} via {t.appliance_name ?? 'appliance'}
                      {t.appliance_online ? '' : ' (offline)'}
                    </Typography>
                  </Box>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ flexShrink: 0, flexWrap: 'wrap', rowGap: 1 }}
                  >
                    {canManage && (
                      <>
                        <Button
                          size="small"
                          onClick={() => {
                            setEditing(t);
                          }}
                          data-testid="edit-target"
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => {
                            setDeleting(t);
                          }}
                          data-testid="delete-target"
                        >
                          Delete
                        </Button>
                      </>
                    )}
                    {allowed && (
                      <Button
                        size="small"
                        variant="contained"
                        disabled={!t.appliance_online || opening !== null || console_ !== null}
                        onClick={() => {
                          openTarget(t);
                        }}
                        data-testid="open-target"
                      >
                        {opening === t.id
                          ? 'Opening…'
                          : t.protocol === 'tcp'
                            ? 'Open terminal'
                            : 'Open'}
                      </Button>
                    )}
                  </Stack>
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}

      <Typography variant="h6" component="h2" sx={{ mt: 4, mb: 1 }}>
        Sessions
      </Typography>
      {sessions.length === 0 ? (
        <Typography color="text.secondary" data-testid="sessions-empty">
          No remote sessions at this site yet.
        </Typography>
      ) : (
        <Stack spacing={1} data-testid="sessions">
          {[...open, ...past].map((s) => (
            <SessionRow
              key={s.id}
              session={s}
              canEnd={s.mine || canEndAny}
              canTranscript={
                s.kind !== 'web' &&
                (s.kind === 'shell' ? platformOperator : s.mine || canReadTranscripts)
              }
              onEnd={() => {
                setEnding(s);
              }}
              onTranscript={() => {
                setTranscript(s);
              }}
            />
          ))}
        </Stack>
      )}

      {editing && (
        <TargetDialog
          target={editing === 'new' ? null : editing}
          appliances={appliances}
          endpoints={endpoints}
          onClose={() => {
            setEditing(null);
          }}
          onSave={async (input) => {
            await saveTarget(
              apiUrl,
              tokenRef.current,
              tenantId,
              siteId,
              input,
              editing === 'new' ? undefined : editing.id,
            );
            setEditing(null);
            await reload();
          }}
        />
      )}
      <ConfirmDialog
        open={deleting !== null}
        title={`Delete ${deleting?.name ?? 'target'}?`}
        confirmLabel="Delete"
        onClose={() => {
          setDeleting(null);
        }}
        onConfirm={async () => {
          if (!deleting) return;
          await deleteTarget(apiUrl, tokenRef.current, tenantId, siteId, deleting.id);
          await reload();
        }}
        describeError={remoteErrorText}
        confirmTestId="confirm-delete-target"
      >
        The appliance stops allowing connections to {deleting?.host}:{deleting?.port}, and open
        sessions to it end now. Past sessions stay in the history.
      </ConfirmDialog>
      <ConfirmDialog
        open={ending !== null}
        title="End this session?"
        confirmLabel="End session"
        onClose={() => {
          setEnding(null);
        }}
        onConfirm={async () => {
          if (!ending) return;
          await endSession(apiUrl, tokenRef.current, tenantId, siteId, ending.id);
          if (console_?.session.id === ending.id) setConsole(null);
          await reload();
        }}
        describeError={remoteErrorText}
        confirmTestId="confirm-end-session"
      >
        {ending?.mine
          ? 'The connection closes now, for you and the appliance.'
          : `${ending?.user ?? 'The user'} is disconnected now. They can open a new session if they still have access.`}
      </ConfirmDialog>
      {shellOpen && (
        <ShellDialog
          appliances={appliances.filter((a) => a.status === 'online')}
          onClose={() => {
            setShellOpen(false);
          }}
          onReauthenticate={onReauthenticate}
          onOpen={async (applianceId, reason) => {
            const o = await openSession(apiUrl, tokenRef.current, tenantId, siteId, {
              kind: 'shell',
              appliance_id: applianceId,
              reason,
            });
            setShellOpen(false);
            if (o.token) setConsole({ session: o.session, token: o.token, driver: null });
            refresh();
          }}
        />
      )}
      {transcript && (
        <TranscriptDialog
          load={() => fetchTranscript(apiUrl, tokenRef.current, tenantId, siteId, transcript.id)}
          session={transcript}
          onClose={() => {
            setTranscript(null);
          }}
        />
      )}
    </Box>
  );
}

// ---------------------------------------------------------------- sessions

function describeTarget(s: RemoteSession): string {
  if (s.kind === 'shell') return `Shell on ${s.appliance_name ?? 'appliance'}`;
  return s.target ? `${s.target.name} (${s.target.host}:${String(s.target.port)})` : 'Target';
}

function SessionRow({
  session: s,
  canEnd,
  canTranscript,
  onEnd,
  onTranscript,
}: {
  session: RemoteSession;
  canEnd: boolean;
  canTranscript: boolean;
  onEnd: () => void;
  onTranscript: () => void;
}) {
  const live = s.status === 'active' || s.status === 'opening';
  const started = s.started_at ?? s.created_at;
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }} data-testid="session" data-status={s.status}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between' }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
              {describeTarget(s)}
            </Typography>
            <Chip
              size="small"
              color={live ? 'success' : s.status === 'failed' ? 'error' : 'default'}
              label={live ? 'Active' : s.status === 'failed' ? 'Failed' : 'Ended'}
              data-testid="session-status"
            />
            {s.kind === 'shell' && <Chip size="small" color="warning" label="Shell" />}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
            {s.mine ? 'You' : s.user} · {new Date(started).toLocaleString()} ·{' '}
            {formatDuration(started, s.ended_at)} · {formatBytes(s.bytes_up)} up,{' '}
            {formatBytes(s.bytes_down)} down
            {s.end_reason ? ` · ${s.end_reason}` : ''}
          </Typography>
          {s.reason && (
            <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
              Reason: {s.reason}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          {canTranscript && (
            <Button size="small" onClick={onTranscript} data-testid="view-transcript">
              Transcript
            </Button>
          )}
          {live && canEnd && (
            <Button size="small" color="error" onClick={onEnd} data-testid="end-session">
              End session
            </Button>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}

function TranscriptDialog({
  session,
  load,
  onClose,
}: {
  session: RemoteSession;
  load: () => Promise<Frame[]>;
  onClose: () => void;
}) {
  const [frames, setFrames] = useState<Frame[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hex, setHex] = useState(false);
  useEffect(() => {
    load()
      .then(setFrames)
      .catch((err: unknown) => {
        setError(remoteErrorText(err));
      });
  }, [load]);
  const decoded = (frames ?? []).map((f) => ({
    ts: f.ts,
    direction: f.direction,
    bytes: fromBase64(f.data),
    cut: f.truncated_bytes ?? 0,
  }));
  const download = () => {
    const blob = new Blob([transcriptText(decoded)], { type: 'text/plain' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `transcript-${session.id}.txt`;
    a.click();
    URL.revokeObjectURL(a.href);
  };
  return (
    <FormDialog
      open
      title={`Transcript: ${describeTarget(session)}`}
      submitLabel="Download"
      canSubmit={decoded.length > 0}
      maxWidth="md"
      onClose={onClose}
      onSubmit={() => {
        download();
        return Promise.resolve();
      }}
      testId="transcript-dialog"
    >
      {error && <Alert severity="error">{error}</Alert>}
      {!frames && !error && <Typography color="text.secondary">Loading…</Typography>}
      {frames && frames.length === 0 && (
        <Typography color="text.secondary">Nothing was sent or received.</Typography>
      )}
      {decoded.length > 0 && (
        <>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={hex ? 'hex' : 'ascii'}
            onChange={(_, v: string | null) => {
              if (v) setHex(v === 'hex');
            }}
            aria-label="Show bytes as"
          >
            <ToggleButton value="ascii">ASCII</ToggleButton>
            <ToggleButton value="hex">Hex</ToggleButton>
          </ToggleButtonGroup>
          <Box
            component="pre"
            data-testid="transcript"
            sx={{
              m: 0,
              p: 1,
              maxHeight: '50vh',
              overflow: 'auto',
              fontFamily: 'monospace',
              fontSize: 13,
              bgcolor: 'action.hover',
              borderRadius: 1,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
            }}
          >
            {decoded
              .map(
                (f) =>
                  `${new Date(f.ts).toLocaleTimeString()} ${f.direction === 'up' ? '>' : '<'} ${
                    hex ? toHex(f.bytes) : toAscii(f.bytes)
                  }${f.cut ? ` … (${String(f.cut)} more bytes not recorded)` : ''}`,
              )
              .join('\n')}
          </Box>
        </>
      )}
    </FormDialog>
  );
}

// ---------------------------------------------------------------- dialogs

const DEFAULT_PORTS: Record<TargetProtocol, number> = { http: 80, https: 443, tcp: 23 };

function TargetDialog({
  target,
  appliances,
  endpoints,
  onSave,
  onClose,
}: {
  target: AccessTarget | null;
  appliances: Appliance[];
  endpoints: Endpoint[];
  onSave: (input: TargetInput) => Promise<void>;
  onClose: () => void;
}) {
  const [name, setName] = useState(target?.name ?? '');
  const [applianceId, setApplianceId] = useState(target?.appliance_id ?? appliances[0]?.id ?? '');
  const [endpointId, setEndpointId] = useState(target?.endpoint_id ?? '');
  const [protocol, setProtocol] = useState<TargetProtocol>(target?.protocol ?? 'http');
  const [host, setHost] = useState(target?.host ?? '');
  const [port, setPort] = useState(String(target?.port ?? DEFAULT_PORTS.http));
  const [portTouched, setPortTouched] = useState(target !== null);
  const [tlsVerify, setTlsVerify] = useState(target?.tls_verify ?? false);
  const [limit, setLimit] = useState(
    target?.max_session_minutes ? String(target.max_session_minutes) : '',
  );
  const portNum = Number(port);
  const limitNum = limit === '' ? null : Number(limit);
  const valid =
    name.trim() !== '' &&
    applianceId !== '' &&
    host.trim() !== '' &&
    Number.isInteger(portNum) &&
    portNum >= 1 &&
    portNum <= 65535 &&
    (limitNum === null || (Number.isInteger(limitNum) && limitNum >= 5 && limitNum <= 240));

  const pickEndpoint = (id: string) => {
    setEndpointId(id);
    const ep = endpoints.find((e) => e.id === id);
    if (!ep || target) return;
    // Driver defaults: the processor's own web interface.
    const s = ep.settings;
    const tls = s.tls === true;
    setApplianceId(ep.appliance_id);
    if (typeof s.host === 'string') setHost(s.host);
    setProtocol(tls ? 'https' : 'http');
    setPort(String(typeof s.port === 'number' ? s.port : tls ? 443 : 80));
    setPortTouched(true);
    if (name.trim() === '') setName(`${ep.name} web interface`);
  };

  return (
    <FormDialog
      open
      title={target ? `Edit ${target.name}` : 'Add target'}
      submitLabel={target ? 'Save' : 'Add'}
      canSubmit={valid}
      onClose={onClose}
      describeError={remoteErrorText}
      onSubmit={() =>
        onSave({
          appliance_id: applianceId,
          endpoint_id: endpointId || null,
          name: name.trim(),
          host: host.trim(),
          port: portNum,
          protocol,
          tls_verify: protocol === 'https' && tlsVerify,
          max_session_minutes: limitNum,
        })
      }
      submitTestId="save-target"
      testId="target-dialog"
    >
      {endpoints.length > 0 && (
        <TextField
          select
          label="Lighting endpoint (optional)"
          value={endpointId}
          onChange={(e) => {
            pickEndpoint(e.target.value);
          }}
          helperText="Fills in the processor's address, and picks its terminal snippets."
          slotProps={{ htmlInput: { 'data-testid': 'target-endpoint' } }}
        >
          <MenuItem value="">None</MenuItem>
          {endpoints.map((e) => (
            <MenuItem key={e.id} value={e.id}>
              {e.name}
            </MenuItem>
          ))}
        </TextField>
      )}
      <TextField
        label="Name"
        value={name}
        onChange={(e) => {
          setName(e.target.value);
        }}
        required
        slotProps={{ htmlInput: { maxLength: 100, 'data-testid': 'target-name' } }}
      />
      <TextField
        select
        label="Reached through"
        value={applianceId}
        onChange={(e) => {
          setApplianceId(e.target.value);
        }}
        required
      >
        {appliances.map((a) => (
          <MenuItem key={a.id} value={a.id}>
            {a.name ?? a.serial}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        label="Type"
        value={protocol}
        onChange={(e) => {
          const p = e.target.value as TargetProtocol;
          setProtocol(p);
          if (!portTouched) setPort(String(DEFAULT_PORTS[p]));
        }}
        slotProps={{ htmlInput: { 'data-testid': 'target-protocol' } }}
      >
        <MenuItem value="http">Web interface (HTTP)</MenuItem>
        <MenuItem value="https">Web interface (HTTPS)</MenuItem>
        <MenuItem value="tcp">TCP terminal</MenuItem>
      </TextField>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          label="Host"
          value={host}
          onChange={(e) => {
            setHost(e.target.value);
          }}
          required
          fullWidth
          helperText="IP address or hostname on the site network"
          slotProps={{ htmlInput: { 'data-testid': 'target-host', autoCapitalize: 'none' } }}
        />
        <TextField
          label="Port"
          value={port}
          onChange={(e) => {
            setPort(e.target.value.replace(/\D/g, ''));
            setPortTouched(true);
          }}
          required
          sx={{ width: { sm: 140 } }}
          slotProps={{ htmlInput: { inputMode: 'numeric', 'data-testid': 'target-port' } }}
        />
      </Stack>
      {protocol === 'https' && (
        <FormControlLabel
          control={
            <Switch
              checked={tlsVerify}
              onChange={(e) => {
                setTlsVerify(e.target.checked);
              }}
            />
          }
          label="Verify the device's certificate (leave off for self-signed certificates)"
        />
      )}
      <TextField
        label="Session limit in minutes (optional)"
        value={limit}
        onChange={(e) => {
          setLimit(e.target.value.replace(/\D/g, ''));
        }}
        helperText="5-240. Sessions otherwise end after 4 hours, or 30 minutes idle."
        slotProps={{ htmlInput: { inputMode: 'numeric' } }}
      />
    </FormDialog>
  );
}

function ShellDialog({
  appliances,
  onOpen,
  onClose,
  onReauthenticate,
}: {
  appliances: Appliance[];
  onOpen: (applianceId: string, reason: string) => Promise<void>;
  onClose: () => void;
  onReauthenticate: () => void;
}) {
  const [applianceId, setApplianceId] = useState(appliances[0]?.id ?? '');
  const [reason, setReason] = useState('');
  const [reauth, setReauth] = useState(false);
  return (
    <FormDialog
      open
      title="Open emergency shell"
      submitLabel="Open shell"
      destructive
      canSubmit={applianceId !== '' && reason.trim().length >= 10}
      onClose={onClose}
      onSubmit={() =>
        onOpen(applianceId, reason.trim()).catch((err: unknown) => {
          if (err instanceof ApiError && err.code === 'reauthentication_required') setReauth(true);
          throw err;
        })
      }
      describeError={remoteErrorText}
      submitTestId="confirm-open-shell"
      testId="shell-dialog"
    >
      <Typography>
        A root-capable shell on the appliance, for diagnosis. Everything typed and shown is
        recorded, and the tenant sees who opened it, when, and why. It ends after 60 minutes.
      </Typography>
      {reauth && (
        <Alert
          severity="info"
          action={
            <Button color="inherit" size="small" onClick={onReauthenticate}>
              Sign in
            </Button>
          }
        >
          Sign in again first: a shell needs a sign-in within the last 5 minutes.
        </Alert>
      )}
      {appliances.length === 0 ? (
        <Alert severity="warning">No appliance at this site is online.</Alert>
      ) : (
        <TextField
          select
          label="Appliance"
          value={applianceId}
          onChange={(e) => {
            setApplianceId(e.target.value);
          }}
        >
          {appliances.map((a) => (
            <MenuItem key={a.id} value={a.id}>
              {a.name ?? a.serial}
            </MenuItem>
          ))}
        </TextField>
      )}
      <TextField
        label="Reason"
        value={reason}
        onChange={(e) => {
          setReason(e.target.value);
        }}
        multiline
        minRows={2}
        required
        helperText="Shown to the tenant. At least 10 characters."
        slotProps={{ htmlInput: { maxLength: 500, 'data-testid': 'shell-reason' } }}
      />
    </FormDialog>
  );
}
